import { useState } from 'react';
import { createPortal } from 'react-dom';
import Modal from '../modal/Modal';
import { CancelSVG } from '../svgs';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, ON_FARM_BILLING_CONSTANTS } from '../../constants/common';
import { OnFarmBillingAgreementStatus } from '../../types/interfaces/onFarmBillingAgreement.interfaces';
import './onFarmBillingAgreementModal.scss';

type OnFarmBillingAgreementModalProps = {
  isOpen: boolean;
  status: OnFarmBillingAgreementStatus;
  submitting?: boolean;
  onClose: () => void;
  onAccept: () => Promise<void> | void;
};

const OnFarmBillingAgreementModal = ({
  isOpen,
  status,
  submitting = false,
  onClose,
  onAccept,
}: OnFarmBillingAgreementModalProps): JSX.Element => {
  const [termsAccepted, setTermsAccepted] = useState<string>(LABEL_CONSTANTS.DECLINED);
  const handleClose = () => {
    if (submitting) return;
    setTermsAccepted(LABEL_CONSTANTS.DECLINED);
    onClose();
  };

  const handleConfirm = async () => {
    if (termsAccepted !== LABEL_CONSTANTS.ACCEPTED || submitting) return;
    await onAccept();
    setTermsAccepted(LABEL_CONSTANTS.DECLINED);
  };

  return createPortal(
    <Modal isOpen={isOpen} onClose={handleClose} ignoreBackdrop>
      <div className="on-farm-billing-agreement-modal">
        <div className="terms-header">
          <h2>{ON_FARM_BILLING_CONSTANTS.AGREEMENT_TITLE}</h2>
          <button className="button icon" type="button" onClick={handleClose} disabled={submitting}>
            <CancelSVG />
          </button>
        </div>

        <div className="terms-body">
          <h3>RSG On-Farm Genetics Inventory Management</h3>
          <p className="terms-subtitle">Subscription Agreement and Terms &amp; Conditions</p>
          <p>
            <strong>Reproduction Specialty Group, Inc. (RSG)</strong>
            <br />
            RSGinventory.com
          </p>
          <p>
            <strong>Effective Date:</strong> November 1, 2026
          </p>

          <h4>1. Description of Services</h4>
          <p>
            Reproduction Specialty Group, Inc. (&quot;RSG&quot;) offers an On-Farm Genetics Inventory Management subscription
            (&quot;On-Farm&quot;) through RSGinventory.com.
          </p>
          <p>
            This service allows livestock breeders, producers, veterinarians, and other authorized customers
            (&quot;Subscriber&quot;) to electronically manage and track semen, embryos, and other reproductive genetic materials
            maintained in their own liquid nitrogen storage tanks.
          </p>
          <p>
            The service provides digital inventory management and recordkeeping only. RSG does not take physical possession of or
            assume responsibility for genetic materials maintained in Subscriber-controlled storage facilities.
          </p>
          <p>
            The service is available to both existing RSG storage customers and Subscribers who do not maintain any genetics at an
            RSG facility.
          </p>

          <h4>2. Monthly Subscription Pricing</h4>
          <p>The standard On-Farm subscription is offered at the following rate:</p>
          <ul>
            <li>
              <strong>Monthly Subscription Fee:</strong> $2.95 total per month.
            </li>
            <li>
              <strong>Included Storage Tanks:</strong> Up to three active on-farm tanks per account.
            </li>
            <li>
              <strong>Inventory Capacity:</strong> Up to 500 individual genetic units per tank.
            </li>
            <li>
              <strong>Maximum Standard Capacity:</strong> 1,500 units across three tanks.
            </li>
            <li>
              <strong>Billing Frequency:</strong> Monthly, after the applicable month.
            </li>
            <li>
              <strong>Subscription Term:</strong> Month-to-month.
            </li>
          </ul>
          <p>
            The $2.95 monthly subscription fee remains the same whether the Subscriber maintains one, two, or three active tanks.
          </p>
          <p>
            The subscription fee covers electronic inventory management services only and does not include physical storage,
            liquid nitrogen, tank maintenance, insurance, or shipping services.
          </p>
          <p>
            <strong>Additional Tanks and Custom Pricing</strong>
          </p>
          <p>Subscribers requiring more than three active tanks must contact RSG for a custom subscription quote.</p>
          <p>Additional tanks may be activated only after RSG approves and establishes a custom plan for the Subscriber.</p>
          <p>
            Custom subscription rates and the approved number of tanks will be established by RSG. Each tank will continue to have
            a maximum electronic inventory capacity of 500 units.
          </p>
          <p>
            Custom plans remain subject to the same billing, inventory management, and tank activation requirements contained in
            this Agreement unless otherwise agreed in writing.
          </p>

          <h4>3. Monthly Billing and Payment Authorization</h4>
          <p>On-Farm subscription fees are billed monthly following the end of the month in which services were provided.</p>
          <p>
            By enrolling, the Subscriber authorizes RSG to charge the designated payment method on file for applicable monthly
            subscription fees.
          </p>
          <p>The following billing conditions apply:</p>
          <ul>
            <li>
              A monthly subscription charge is assessed if at least one On-Farm tank was active at any time during the billing
              month.
            </li>
            <li>Subscription fees are not prorated for partial months.</li>
            <li>Adding or removing a tank during a billing month does not result in an additional charge, credit, or refund.</li>
            <li>An active tank is billable even if it contains no genetic inventory.</li>
            <li>
              If no tanks were active at any time during a billing month, no On-Farm subscription fee will be assessed for that
              month.
            </li>
            <li>Subscription charges continue for each month in which at least one tank remains active.</li>
          </ul>
          <p>The Subscriber agrees to maintain a valid payment method on file with RSG.</p>
          <p>
            RSG reserves the right to revise subscription pricing upon at least 30 days&apos; advance notice to the Subscriber.
          </p>

          <h4>4. Separate Billing for RSG Facility Storage</h4>
          <p>
            On-Farm Genetics Inventory Management is a separate service from physical genetic storage provided at an RSG facility.
          </p>
          <p>Subscribers utilizing both services will receive separate monthly invoices for:</p>
          <ul>
            <li>RSG Facility Storage — genetics physically stored and maintained by RSG.</li>
            <li>RSG On-Farm Inventory Management — genetics maintained in Subscriber-controlled storage tanks.</li>
          </ul>
          <p>
            The same payment method may be used for both services; however, invoices, payments, outstanding balances, and account
            restrictions are managed independently.
          </p>
          <p>Payment of one service does not satisfy outstanding charges associated with the other.</p>

          <h4>5. Storage Tank Management and Inventory Limits</h4>
          <p>Subscribers may establish and maintain electronic records for their authorized On-Farm storage tanks.</p>
          <p>Each tank is subject to a maximum inventory capacity of 500 individual semen or embryo units.</p>
          <p>The Subscriber acknowledges and agrees that:</p>
          <ul>
            <li>Inventory additions cannot exceed the 500-unit limit for an individual tank.</li>
            <li>Genetic materials may be added or transferred only to active storage locations, tanks, and canisters.</li>
            <li>A tank must have no recorded inventory before it can be designated inactive.</li>
            <li>
              Before deactivating a tank, all recorded units must be transferred, withdrawn, used, or otherwise appropriately
              removed from the electronic inventory.
            </li>
            <li>Deactivating a tank does not generate a refund or credit for the current billing month.</li>
            <li>Additional tanks beyond the standard three-tank limit require an approved custom subscription.</li>
          </ul>
          <p>
            The electronic inventory capacity established by RSG does not represent a determination or guarantee of the physical
            storage capacity of any tank.
          </p>

          <h4>6. Subscriber Responsibility for Physical Storage</h4>
          <p>
            The Subscriber retains sole responsibility for the physical storage, handling, maintenance, and protection of all
            genetic materials maintained in their possession.
          </p>
          <p>These responsibilities include, but are not limited to:</p>
          <ul>
            <li>Maintaining appropriate liquid nitrogen levels.</li>
            <li>Monitoring and maintaining suitable cryogenic storage conditions.</li>
            <li>Inspecting and maintaining storage tanks and equipment.</li>
            <li>Properly identifying and labeling genetic materials.</li>
            <li>Maintaining appropriate tank, canister, and inventory organization.</li>
            <li>Protecting genetic materials from loss, theft, contamination, damage, or deterioration.</li>
            <li>Ensuring the safe handling and use of cryogenic storage equipment.</li>
            <li>Maintaining accurate electronic inventory records.</li>
          </ul>
          <p>
            RSG does not monitor or verify liquid nitrogen levels, storage temperatures, equipment condition, or the physical
            presence of genetic materials maintained on-farm.
          </p>
          <p>
            The Subscriber acknowledges that participation in this program does not establish a physical storage, custody, or
            preservation relationship between the Subscriber and RSG.
          </p>

          <h4>7. Inventory Accuracy and Recordkeeping</h4>
          <p>
            The Subscriber is responsible for entering, updating, and verifying all information maintained within their On-Farm
            inventory account.
          </p>
          <p>RSG does not independently verify the accuracy of inventory records entered by Subscribers.</p>
          <p>
            Electronic inventory information may include animal identification, genetic material type, quantity, location,
            ownership, and transaction history.
          </p>
          <p>
            The Subscriber is responsible for recording inventory deposits, transfers, withdrawals, use, and disposal accurately
            and promptly.
          </p>
          <p>
            RSG does not guarantee the accuracy, completeness, physical existence, genetic identity, fertility, or viability of
            materials recorded in the system.
          </p>

          <h4>8. Ownership and Control of Genetic Materials</h4>
          <p>
            All genetic materials maintained in Subscriber-controlled tanks remain under the possession and control of the
            Subscriber or their respective legal owners.
          </p>
          <p>Use of RSGinventory.com does not transfer ownership, title, or possession of genetic materials to RSG.</p>
          <p>
            Subscribers are responsible for ensuring they possess the appropriate ownership rights or authorization to store,
            manage, use, transfer, or sell materials recorded in their accounts.
          </p>
          <p>
            RSG is not responsible for resolving ownership disputes concerning genetic materials maintained in
            Subscriber-controlled tanks.
          </p>

          <h4>9. Failed Payments and Account Restrictions</h4>
          <p>If payment for the On-Farm subscription is unsuccessful, the outstanding invoice will remain payable.</p>
          <p>
            While an On-Farm invoice remains unpaid, the Subscriber&apos;s On-Farm account will be restricted to{' '}
            <strong>view-only access</strong>.
          </p>
          <p>
            During this restriction, Subscribers may continue to view their existing On-Farm inventory, storage tanks, and
            transaction history but cannot create, update, delete, deposit, transfer, or otherwise modify On-Farm inventory
            records.
          </p>
          <p>
            Once the outstanding On-Farm invoice is paid or the balance is cleared by authorized RSG personnel, editing access
            will be restored unless RSG has separately blocked the On-Farm account.
          </p>
          <p>An unpaid On-Farm subscription does not automatically restrict access to RSG facility inventory services.</p>
          <p>
            Similarly, an unpaid RSG facility storage invoice does not automatically restrict access to On-Farm inventory
            management.
          </p>
          <p>
            RSG reserves the right to separately restrict On-Farm account editing privileges when reasonably necessary for account
            security, suspected misuse, or violations of this Agreement.
          </p>

          <h4>10. Cancellation and Tank Deactivation</h4>
          <p>The On-Farm subscription operates on a month-to-month basis without a long-term subscription commitment.</p>
          <p>
            Subscribers may discontinue the service by removing all recorded inventory from their active On-Farm tanks and
            designating those tanks inactive.
          </p>
          <p>A tank cannot be designated inactive while it contains recorded inventory.</p>
          <p>
            If one or more tanks were active at any time during the current billing month, the applicable subscription charge
            remains payable for that month.
          </p>
          <p>No subscription fee will be assessed for subsequent months in which no tanks are active.</p>
          <p>
            Subscribers are encouraged to export or retain copies of their inventory records before discontinuing the service.
          </p>
          <p>Cancellation of the On-Farm subscription does not cancel or modify any separate RSG facility storage agreement.</p>

          <h4>11. Insurance and Risk of Loss</h4>
          <p>
            The On-Farm subscription does not include insurance coverage for semen, embryos, storage tanks, or other genetic
            materials maintained by the Subscriber.
          </p>
          <p>
            The Subscriber assumes all risks associated with the physical storage and preservation of genetic materials in their
            possession.
          </p>
          <p>
            Any insurance coverage offered through RSG or another provider is separate from the On-Farm subscription and subject
            to the terms, conditions, limitations, and premiums of the applicable insurance policy.
          </p>
          <p>
            Shipping and insurance charges associated with genetic materials transported from RSG facilities are billed
            separately.
          </p>

          <h4>12. Limitation of Liability</h4>
          <p>
            The Subscriber acknowledges that RSG provides electronic inventory management services only and is not responsible for
            the physical storage, handling, or preservation of genetic materials maintained in Subscriber-controlled tanks.
          </p>
          <p>To the fullest extent permitted by applicable law, RSG shall not be liable for loss or damage resulting from:</p>
          <ul>
            <li>Storage tank failure, liquid nitrogen depletion, or equipment malfunction.</li>
            <li>Improper handling, storage, or maintenance of genetic materials.</li>
            <li>Loss of fertility, viability, or reproductive performance.</li>
            <li>Theft, contamination, destruction, or accidental loss of genetic materials.</li>
            <li>Inaccurate, incomplete, or outdated inventory information entered by the Subscriber.</li>
            <li>Unauthorized use, removal, or transfer of genetic materials by the Subscriber or third parties.</li>
            <li>Subscriber errors or omissions in maintaining inventory records.</li>
          </ul>
          <p>
            To the fullest extent permitted by applicable law, RSG shall not be liable for indirect, incidental, consequential, or
            special damages, including lost profits, lost breeding opportunities, or loss of genetic value arising from the use of
            the On-Farm service.
          </p>
          <p>
            RSG&apos;s total aggregate liability arising from the On-Farm subscription shall not exceed the total subscription
            fees paid by the Subscriber during the 12 months preceding the event giving rise to the claim, to the extent permitted
            by law.
          </p>
          <p>Nothing in this Agreement limits liability that cannot legally be excluded or limited.</p>

          <h4>13. System Availability and Data Security</h4>
          <p>
            RSG will use commercially reasonable efforts to maintain the availability, security, and functionality of
            RSGinventory.com.
          </p>
          <p>RSG does not guarantee uninterrupted access to the platform.</p>
          <p>
            Service interruptions may occur due to maintenance, system upgrades, internet connectivity, technical failures,
            cybersecurity incidents, or circumstances beyond RSG&apos;s reasonable control.
          </p>
          <p>
            Subscribers are responsible for maintaining the confidentiality of their account credentials and promptly notifying
            RSG of suspected unauthorized access.
          </p>
          <p>Subscribers are encouraged to maintain independent copies of important inventory records.</p>

          <h4>14. Inventory Data and Privacy</h4>
          <p>Subscribers retain their rights in the inventory information they enter into RSGinventory.com.</p>
          <p>
            By using the service, Subscribers authorize RSG to store, process, and maintain their inventory records as necessary
            to operate and support the On-Farm platform.
          </p>
          <p>
            RSG may use aggregated or de-identified inventory information for internal analysis, system improvement, and business
            planning, provided such use does not identify individual Subscribers or disclose their confidential inventory records.
          </p>
          <p>RSG will not treat inventory information as authorization to sell or transfer ownership of genetic materials.</p>

          <h4>15. Modifications to Services and Agreement</h4>
          <p>
            RSG reserves the right to update platform features, subscription pricing, inventory limitations, and these Terms and
            Conditions.
          </p>
          <p>Subscribers will receive reasonable advance notice of material changes.</p>
          <p>
            Continued use of the On-Farm service after the effective date of properly communicated revised terms constitutes
            acceptance to the extent permitted by applicable law.
          </p>

          <h4>16. Governing Law</h4>
          <p>
            This Agreement shall be governed by the laws of the State of Indiana, without regard to conflict-of-law provisions.
          </p>
          <p>
            Disputes arising from this Agreement shall be subject to the jurisdiction of the appropriate state or federal courts
            located in Indiana, except as otherwise required by law.
          </p>

          <h4>17. Electronic Acceptance and Authorization</h4>
          <p>
            By activating an On-Farm storage tank and accepting these Terms and Conditions, the Subscriber acknowledges and agrees
            that:
          </p>
          <ul>
            <li>The On-Farm subscription provides electronic inventory management only.</li>
            <li>The standard monthly fee is $2.95 total for up to three active tanks, with a maximum of 500 units per tank.</li>
            <li>Subscription charges are assessed monthly after the billing month, without proration.</li>
            <li>
              A subscription charge applies for any month in which at least one tank was active, regardless of whether that tank
              contained inventory.
            </li>
            <li>
              The Subscriber remains solely responsible for the physical storage, maintenance, security, and preservation of their
              genetic materials.
            </li>
            <li>
              The Subscriber authorizes RSG to charge the designated payment method for applicable monthly subscription fees.
            </li>
            <li>Failure to pay On-Farm subscription charges may result in view-only account restrictions.</li>
            <li>The Subscriber has read, understands, and agrees to these Terms and Conditions.</li>
          </ul>
          <p>
            <strong>Reproduction Specialty Group, Inc.</strong>
            <br />
            RSGinventory.com
          </p>
          <p>
            <em>© 2026 Reproduction Specialty Group, Inc. All rights reserved.</em>
          </p>

          <div className="terms-accept-checkbox">
            <input
              type="checkbox"
              id="onFarmBillingTermsAccepted"
              disabled={submitting}
              checked={termsAccepted === LABEL_CONSTANTS.ACCEPTED}
              onChange={() =>
                setTermsAccepted(prev =>
                  prev === LABEL_CONSTANTS.DECLINED ? LABEL_CONSTANTS.ACCEPTED : LABEL_CONSTANTS.DECLINED,
                )
              }
            />
            <label htmlFor="onFarmBillingTermsAccepted">{ON_FARM_BILLING_CONSTANTS.AGREEMENT_CHECKBOX}</label>
          </div>
        </div>

        <div className="terms-footer flex-right">
          <button
            className="button accept-btn small"
            type="button"
            onClick={handleConfirm}
            disabled={termsAccepted !== LABEL_CONSTANTS.ACCEPTED || submitting}>
            {BUTTON_CONSTANTS.CONFIRM}
          </button>
        </div>
      </div>
    </Modal>,
    document.body,
  );
};

export default OnFarmBillingAgreementModal;
